import { useCallback, useMemo } from "react";

import { useLocalStorage } from "lib/hooks/useLocalStorage";
import { ColumnSetting } from "lib/types/models";

type StoredView<F extends string, S> = {
  columns?: F[];
  sort?: S;
};

/**
 * Persists a list page's view preferences (visible columns + their order, and the
 * active sort) to localStorage, keyed per page. Columns are stored as ordered field
 * keys and rehydrated against the canonical column definitions, so renamed titles,
 * reordered defaults, or removed columns can't surface stale labels or break the table.
 */
export function usePersistedTableView<F extends string, S>(
  storageKey: string,
  allAvailableColumns: ColumnSetting<F>[],
  defaultVisibleColumns: ColumnSetting<F>[],
): {
  appliedColumns: ColumnSetting<F>[];
  setColumns: (columns: ColumnSetting<F>[]) => void;
  storedSort: S | undefined;
  setSort: (sort: S) => void;
} {
  const [stored, setStored] = useLocalStorage<StoredView<F, S> | null>(storageKey, null);

  const appliedColumns = useMemo(() => {
    const fields = stored?.columns;
    if (!fields) return defaultVisibleColumns;

    const byField = new Map(allAvailableColumns.map((c) => [c.field, c]));
    const resolved = fields
      .map((field) => byField.get(field))
      .filter((column): column is ColumnSetting<F> => Boolean(column));

    return resolved.length > 0 ? resolved : defaultVisibleColumns;
  }, [stored, allAvailableColumns, defaultVisibleColumns]);

  const setColumns = useCallback(
    (columns: ColumnSetting<F>[]) =>
      setStored((prev) => ({ ...prev, columns: columns.map((c) => c.field) })),
    [setStored],
  );

  const setSort = useCallback((sort: S) => setStored((prev) => ({ ...prev, sort })), [setStored]);

  return { appliedColumns, setColumns, storedSort: stored?.sort, setSort };
}
